<script>
import dayjs from '../../core/dayjs';

export default {
  props: {
    attendences: {
      type: Array,
      required: true,
    },
    lastPage: {
      type: Number,
      required: true,
    },
    lead: {
      type: Object,
      required: true,
    },
  },

  data () {
    return {
      isLoadingSyngooUrl: false,
      isLoadingAttendences: false,
      syngooUrl: null,
      currentPage: 1,
    };
  },

  created () {
    this.getSyngooUrl();
  },

  computed: {
    attendencesLink () {
      return `${this.syngooUrl}/user/agent`;
    },
  },

  methods: {
    formatDate (date) {
      if (!date) {
        return 'Data indefinida';
      }

      try {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
      } catch (error) {
        return 'Data inválida';
      }
    },

    openDocsModal () {
      Bus.$emit('openDocsModal', 'syngoo');
    },

    openMediaModal () {
      Bus.$emit('openMediaModal', 'syngoo');
    },

    getSyngooUrl () {
      this.isLoadingSyngooUrl = true;

      this.$http.get('/integration/config/syngoo').then(({ body }) => {
        if (!body.config) {
          return null;
        }

        this.syngooUrl = body.config.url;
      }).catch(() => {
        console.error('Erro ao buscar configurações da integração');
      }).finally(() => {
        this.isLoadingSyngooUrl  == false;
      });
    },

    loadMore () {
      this.isLoadingAttendences = true;

      this.currentPage += 1;

      this.$http.get(`/lead/attendence/${this.lead.hashid}?page=${this.currentPage}`)
      .then(({ body }) => {
        this.attendencesLastPage = body.last_page;
        this.currentPage = body.current_page;

        this.attendences.push(...body.data);
      }).catch(() => {
        Alert.error('Falha ao carregar atendimentos');
      }).finally(() => {
        this.isLoadingAttendences = false;
      });
    },
  }
}
</script>

<template>
  <div id="attendences-container">
    <header class="attendences-header px-2 pb-2">
      <a
        :href="attendencesLink"
        target="_blank"
        class="attendences-header-link d-flex align-items-center"
        :class="{ 'disabled-link': !syngooUrl }"
      >
        <IconChatDots class="mr-2" />
        <span>
          {{ __('Abrir atendimentos') }}
        </span>
      </a>
      <div>
        <button
          @click="openDocsModal"
          class="attendences-header-button"
        >
          <IconPaperclip />
        </button>
        <button
          @click="openMediaModal"
          class="attendences-header-button ml-3"
        >
          <IconImage />
        </button>
      </div>
    </header>
    <section
      v-for="attendence in attendences"
      class="attendence mt-12px pt-2 px-2"
    >
      <header
        class="attendence-header pb-12px"
      >
        <div class="attendant">
          <Avatar
            :name="attendence.attendant"
            width="32"
            class="rounded-circle"
          />
          <span
            class="attendant-name"
          >
            {{ attendence.attendant }}
          </span>
        </div>
        <div
          class="attendence-time"
        >
          <i class="fa fa-clock-o mr-2" />
          <span>{{ __('Início:') }} {{ formatDate(attendence.start) }}</span>
          <div class="separator mx-12px" />
          <span>{{ __('Final:') }} {{ formatDate(attendence.end) }}</span>
        </div>
      </header>
      <div class="attendence-messages pt-1">
        <LeadAttendenceMessage
          v-for="message in attendence.messages"
          :attendant="attendence.attendant"
          :lead="lead"
          :message="message"
        />
      </div>
      <footer class="attendence-footer d-flex p-2">
        <span class="ml-auto">
          {{ __('Via integração') }} {{ attendence.plataform.title }}
        </span>
      </footer>
    </section>
    <div v-if="currentPage < lastPage" class="load-more">
      <button
        v-if="!isLoadingAttendences"
        @click="loadMore"
      >
        {{ __('Ver mais') }}
      </button>
      <i v-else class="fa fa-circle-o-notch fa-spin" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import '../../../sass/variables';
  @import '../../../sass/mixins';

  #attendences-container {
    width: 100%;
    padding: 16px;
    background-color: white;
    border-radius: 16px;
    box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.1);

    .attendences-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid $salesflow-outline-gray-medium;

      .attendences-header-link {
        @include salesflow-text($font-weight: 600, $color: $salesflow-font-gray);

        &:hover {
          color: $salesflow-primary-hover;

          ::v-deep svg path {
            fill: $salesflow-primary-hover !important;
          }
        }
      }

      .attendences-header-button {
        margin: 0px;
        padding: 0px;
        border: 0px;
        outline: 0px;
        line-height: 100%;

        svg {
          background-color: white;
        }

        &:hover ::v-deep svg path {
          fill: $salesflow-primary-hover !important;
        }

        &:focus {
          outline: 0px;
        }
      }
    }

    .attendence {
      .attendence-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid $salesflow-outline-gray-medium;

        .attendant {
          display: flex;
          align-items: center;
          gap: 8px;

          .attendant-name {
            @include salesflow-text($font-weight: 600);
            font-family: 'Inter';
            font-size: 14px;
            max-width: 16rem;
          }
        }

        .attendence-time {
          display: flex;
          align-items: center;
          @include salesflow-text($color: $salesflow-font-gray);
          font-family: 'Inter';

          i {
            width: 12px;
          }

          .separator {
            width: 1px;
            height: 1rem;
            background-color: $salesflow-outline-gray-medium;
          }
        }
      }

      .attendence-messages {
        max-height: 34rem;
        overflow-y: scroll;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      .attendence-footer {
        border-top: 1px solid $salesflow-outline-gray-medium;
        @include salesflow-text($color: $salesflow-font-gray);
        font-family: 'Inter';
      }
    }
  }

  .disabled-link {
    pointer-events: none;
  }

  .load-more {
    width: 100%;
    display: flex;
    align-self: center;
    justify-content: center;
    padding: 12px;

    button {
      background: transparent;
      border: 0px;
      outline: 0px;

      @include salesflow-text;

      &:hover {
        color: $salesflow-primary-hover;
      }
    }
  }
</style>
